import React from "react";
import { useTranslation } from "react-i18next";
import sendolaBusinessLogo from "@/assets/Img/sendolapay-business-logo-white.png";
import BusinessGreen from "@/assets/Icons/business-green.svg?react";
import CheckBusinessIcon from "@/assets/Icons/check-business.svg?react";
import classNames from "classnames";

type CountryCode = "USA" | "CAN";

interface SendolaBusinessCardProps {
  country: CountryCode;
  className?: string;
  onClickLearnMore?: () => void;
}

export const SendolaBusinessCard: React.FC<SendolaBusinessCardProps> = ({
  country,
  className,
  onClickLearnMore,
}) => {
  const { t } = useTranslation("cabo");

  return (
    <div
      className={classNames(
        "bg-dark-gray-500 rounded-[2.5rem] w-full mx-auto flex flex-col items-center gap-4 md:gap-6",
        className
      )}
    >
      <img
        src={sendolaBusinessLogo}
        alt="SendolaBusiness Logo"
        className="h-16 sm:h-20 mb-2 mt-2"
      />

      <div
        className="w-full rounded-3xl border-[0.5px] border-business-green3 bg-dark-gray-500 px-4 py-2 flex flex-col items-center gap-2 scale-[1.02] shadow-business-green3"
        style={{ boxShadow: "0 0 4px 2px #00000088" }}
      >
        <div className="flex items-center gap-3 w-full justify-center">
          <BusinessGreen className="[&_path]:fill-business-green3 w-8 h-8 flex-shrink-0" />
          <div className="flex flex-col text-center">
            <span className="font-semibold sm:text-lg md:text-xl text-white">
              {t("solutions.business.forBusiness")}
            </span>
            <span className="font-bold text-sm sm:text-base md:text-lg text-business-green3 leading-tight">
              {t("solutions.business.customSolution")}
            </span>
            <span className="text-xs text-business-green3 mt-1">
              ({t("solutions.business.noLimits")})
            </span>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-2 md:gap-4 w-full mt-2 p-6 sm:p-10">
        <div className="flex items-start gap-2">
          <CheckBusinessIcon className="h-6 w-6 flex-shrink-0" />
          <div className="flex flex-col items-start">
            <span className="text-white text-base sm:text-lg">
              {country === "CAN"
                ? t("solutions.business.transferFundsCAN")
                : t("solutions.business.transferFundsUS")}
            </span>
            <span className="text-xs text-gray-500">
              ({t("solutions.business.otherCountriesSoon")})
            </span>
          </div>
        </div>
        <div className="flex items-start gap-2">
          <CheckBusinessIcon className="h-6 w-6 flex-shrink-0" />
          <span className="text-white text-base sm:text-lg flex items-center gap-1">
            {t("solutions.business.zeroTransferFees")}
          </span>
        </div>
        <div className="flex items-start gap-2">
          <CheckBusinessIcon className="h-6 w-6 flex-shrink-0" />
          <span className="text-white text-base sm:text-lg">
            {t("solutions.business.dedicatedManagers")}
          </span>
        </div>
        <div className="flex items-start gap-2">
          <CheckBusinessIcon className="h-6 w-6 flex-shrink-0" />
          <span className="text-white text-base sm:text-lg">
            {t("solutions.business.transparentFX")}
          </span>
        </div>
        <span className="text-xs text-gray-500 ml-8">
          *{t("solutions.business.conciergeAdvice")}
        </span>
      </div>

      <button
        className="mt-auto mb-8 bg-dark-gray-500 border border-business-green3 border-opacity-35 rounded-full px-10 py-2 font-medium text-lg text-white hover:bg-dark-gray-900 hover:border-opacity-50 transition-all shadow-sm"
        onClick={onClickLearnMore}
      >
        {t("learnMore")}
      </button>
    </div>
  );
};
